import { Inject, Injectable, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, concat, interval, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { VERSION_CHECK_INTERVAL_TIME } from '../models/version-check-interval-time.token';

@Injectable({
	providedIn: 'root'
})
export class VersionCheckWorkerService implements OnDestroy {
	// BehaviorSubject to track if a new update is available
	private isAnyNewUpdateAvailable: BehaviorSubject<boolean> = new BehaviorSubject(false);

	// Array to keep track of service subscriptions
	private serviceSubscriptions: Subscription[] = [];

	constructor(
		private swUpdate: SwUpdate,
		@Inject(VERSION_CHECK_INTERVAL_TIME) private updateIntervalMs: number // Inject the interval
	) {
		// Initialize the update check process if Service Worker is enabled
		if (this.swUpdate.isEnabled) {
			this.initiateUpdateCheck();
			this.subscribeToVersionUpdates();
		}
	}

	/**
	 * Initiates periodic checks for updates.
	 * Sets up a process to check for updates every 1 hour.
	 */
	private initiateUpdateCheck(): void {
		const appIsStable$ = interval(this.updateIntervalMs).pipe(first());
		const everyParamsHours$ = concat(appIsStable$, interval(this.updateIntervalMs));
		const checkForUpdateSub = everyParamsHours$.subscribe(() => {
			this.swUpdate.checkForUpdate().then(() => {});
		});
		this.serviceSubscriptions.push(checkForUpdateSub);
	}

	/**
	 * Subscribes to version update events.
	 * Listens for new version availability and updates the BehaviorSubject.
	 */
	private subscribeToVersionUpdates(): void {
		const versionUpdatesSub = this.swUpdate.versionUpdates.subscribe(event => {
			if (event.type === 'VERSION_READY') {
				this.isAnyNewUpdateAvailable.next(true);
			}
		});
		this.serviceSubscriptions.push(versionUpdatesSub);
	}

	/**
	 * Activates the update and reloads the application.
	 * Forces the service worker to activate the new version and reload the app.
	 */
	public activateUpdate(): void {
		this.swUpdate.activateUpdate().then(() => document.location.reload());
	}

	/**
	 * Unsubscribes from all subscriptions to prevent memory leaks.
	 */
	ngOnDestroy(): void {
		this.serviceSubscriptions.forEach(sub => sub.unsubscribe());
	}

	/**
	 * Returns an observable to check for new updates.
	 */
	public get updateAvailable$(): BehaviorSubject<boolean> {
		return this.isAnyNewUpdateAvailable;
	}
}
